import { useState } from 'react';
import { Button } from './ui/button';
import {
  LayoutDashboard,
  Users,
  MapPin,
  Clock,
  AlertCircle,
  FileText,
  Database,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const ManagerLayout = ({ children, manager, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/manager/dashboard',
      icon: LayoutDashboard,
      testId: 'nav-dashboard',
    },
    {
      name: 'Employees',
      path: '/manager/employees',
      icon: Users,
      testId: 'nav-employees',
    },
    {
      name: 'Locations',
      path: '/manager/locations',
      icon: MapPin,
      testId: 'nav-locations',
    },
    {
      name: 'Timesheets',
      path: '/manager/timesheets',
      icon: Clock,
      testId: 'nav-timesheets',
    },
    {
      name: 'Exceptions',
      path: '/manager/exceptions',
      icon: AlertCircle,
      testId: 'nav-exceptions',
    },
    {
      name: 'Edit Log',
      path: '/manager/edit-log',
      icon: FileText,
      testId: 'nav-edit-log',
    },
    {
      name: 'Data Management',
      path: '/manager/data-management',
      icon: Database,
      testId: 'nav-data-management',
      adminOnly: true,
    },
  ];

  const handleLogout = () => {
    onLogout();
    navigate('/manager/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white shadow-md p-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-800">Punch Clock</h1>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-testid="mobile-menu-toggle"
        >
          {sidebarOpen ? <X /> : <Menu />}
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-xl transition-transform duration-300 ease-in-out`}
        >
          <div className="p-6 border-b hidden lg:block">
            <h1 className="text-2xl font-bold text-gray-800">Punch Clock</h1>
            <p className="text-sm text-gray-600 mt-1">Manager Portal</p>
          </div>

          <nav className="p-4 space-y-2">
            {menuItems
              .filter((item) => !item.adminOnly || manager?.role === 'Administrator')
              .map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                      isActive
                        ? 'bg-gradient-to-r from-gray-900 to-black text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    data-testid={item.testId}
                  >
                    <item.icon className="w-5 h-5" />
                    <span className="font-medium">{item.name}</span>
                  </button>
                );
              })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t bg-white">
            <div className="mb-3 px-2">
              <p className="text-sm text-gray-600">Logged in as</p>
              <p className="font-semibold text-gray-800">{manager?.full_name}</p>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full flex items-center gap-2 justify-center"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 lg:p-8">{children}</main>
      </div>
    </div>
  );
};

export default ManagerLayout;